import { useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ActionBar, type DesktopAction } from '@/components/desktop';
import { ActionDialog } from '@/components/hr/ActionDialog';
import { printTable } from '@/components/hrPrint';
import { DialogSubject, ErrorNote, OkNote, errorMessage, shortDate } from '@/components/ui';
import { MARK_CODE, MARK_TEXT, WORKER_TYPE_TEXT, monthName, useHrInvalidation, type Mark, type WorkerType } from '@/hooks/hr';
import { daysText, hrPut } from './hrScreenHelpers';

export interface MonthCell {
  mark: Mark;
  leave: string | null;
  elsewhere: boolean;
  overtime: boolean;
}

export interface MonthOverride {
  id: string;
  presentDays: string;
  absentDays: string;
  paidLeaveDays: string;
  unpaidLeaveDays: string;
  overtimeHours: string;
  reason: string;
}

export interface MonthRow {
  employeeId: string;
  code: string;
  nameEn: string;
  designation: string | null;
  workerType: WorkerType;
  cells: Array<MonthCell | null>;
  totals: {
    present: number;
    absent: number;
    halfDay: number;
    leave: number;
    off: number;
    overtimeHours: string;
    unmarked: number;
  };
  override: MonthOverride | null;
  lockedBy: string | null;
}

export interface MonthData {
  year: number;
  month: number;
  dates: string[];
  holidays: Record<string, string>;
  rows: MonthRow[];
}

const WEEKDAY_LETTER = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function cellKind(mark: Mark): string {
  switch (mark) {
    case 'PRESENT':
      return 'present';
    case 'ABSENT':
      return 'absent';
    case 'HALF_DAY':
      return 'half';
    case 'LEAVE':
      return 'leave';
    default:
      return 'off';
  }
}

function cellTitle(date: string, c: MonthCell | null, holiday: string | undefined): string {
  const parts = [shortDate(date)];
  if (holiday) parts.push(`Holiday: ${holiday}`);
  if (!c) parts.push('Not marked');
  else {
    parts.push(c.mark === 'LEAVE' && c.leave ? `Leave (${c.leave})` : MARK_TEXT[c.mark]);
    if (c.overtime) parts.push('Overtime');
    if (c.elsewhere) parts.push('Worked on another farm');
  }
  return parts.join(' · ');
}

function overrideText(o: MonthOverride): string {
  return `Present ${daysText(o.presentDays)}, absent ${daysText(o.absentDays)}, paid leave ${daysText(o.paidLeaveDays)}, unpaid leave ${daysText(o.unpaidLeaveDays)}, overtime ${daysText(o.overtimeHours)} hours`;
}

/**
 * The month at a glance: everyone × every day, read-only. Clicking a day
 * opens the daily register on it. A person whose farm keeps a paper register
 * can have the whole month typed in instead.
 */
export function AttendanceMonth({
  data,
  loading,
  farmLabel,
  today,
  canUpdate,
  onOpenDate,
}: {
  data: MonthData | undefined;
  loading: boolean;
  farmLabel: string;
  today: string;
  canUpdate: boolean;
  onOpenDate: (date: string) => void;
}) {
  const invalidate = useHrInvalidation();
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const rows = data?.rows ?? [];
  const dates = data?.dates ?? [];
  const holidays = data?.holidays ?? {};
  const chosen = rows.find((r) => r.employeeId === chosenId) ?? null;
  const title = data ? `${monthName(data.month)} ${data.year}` : '';
  const started = dates.length > 0 && dates[0]! <= today;

  async function removeOverride(row: MonthRow) {
    if (!row.override) return;
    if (!window.confirm(`Remove the typed-in month for ${row.nameEn}? Pay will use the daily register again.`)) return;
    try {
      await api.delete(`/api/v1/hr/attendance/month-override/${row.override.id}`);
      invalidate();
      setProblem(null);
      setNote(`Typed-in month removed for ${row.nameEn}. Pay uses the daily register again.`);
    } catch (e) {
      setProblem(errorMessage(e, 'Could not remove it'));
    }
  }

  function sheetRows() {
    return rows.map((r) => {
      const out: Record<string, unknown> = {
        code: r.code,
        name: r.nameEn,
        designation: r.designation ?? '',
        present: r.totals.present,
        absent: r.totals.absent,
        half: r.totals.halfDay,
        leave: r.totals.leave,
        off: r.totals.off,
        unmarked: r.totals.unmarked,
        overtime: r.totals.overtimeHours,
        typedIn: r.override ? overrideText(r.override) : '',
      };
      r.cells.forEach((c, i) => {
        out[`d${i + 1}`] = c ? MARK_CODE[c.mark] : '';
      });
      return out;
    });
  }

  function print() {
    if (!data) return;
    printTable({
      title: `Attendance — ${farmLabel} — ${title}`,
      subtitle: 'P present · A absent · H half day · L leave · O weekly off · HD holiday',
      columns: [
        { key: 'code', header: 'Code' },
        { key: 'name', header: 'Name' },
        ...dates.map((_, i) => ({ key: `d${i + 1}`, header: String(i + 1) })),
        { key: 'present', header: 'P', kind: 'number' as const },
        { key: 'absent', header: 'A', kind: 'number' as const },
        { key: 'half', header: 'H', kind: 'number' as const },
        { key: 'leave', header: 'L', kind: 'number' as const },
        { key: 'off', header: 'Off', kind: 'number' as const },
        { key: 'overtime', header: 'OT hours', kind: 'number' as const },
      ],
      rows: sheetRows(),
      landscape: true,
      signatures: ['Supervisor', 'Checked by'],
    });
  }

  const chooseFirst = 'Choose a person first (click their name)';
  const actions: DesktopAction[] = [
    {
      label: 'Type in a month',
      icon: 'edit',
      onSelect: () => setTyping(true),
      allowed: canUpdate,
      enabled: !!chosen && !chosen.lockedBy && started,
      reason: !chosen
        ? chooseFirst
        : chosen.lockedBy
          ? `Pay run ${chosen.lockedBy} covers this month`
          : 'A month cannot be typed in before it has started',
    },
    {
      label: 'Remove typed-in month',
      icon: 'remove',
      danger: true,
      onSelect: () => {
        if (chosen) void removeOverride(chosen);
      },
      allowed: canUpdate,
      enabled: !!chosen?.override && !chosen.lockedBy,
      reason: !chosen
        ? chooseFirst
        : chosen.lockedBy
          ? `Pay run ${chosen.lockedBy} covers this month`
          : 'This person has no typed-in month',
    },
    {
      label: 'Print month sheet',
      icon: 'print',
      onSelect: print,
      enabled: rows.length > 0,
      reason: 'Nobody to print',
      separatorBefore: true,
    },
  ];

  return (
    <>
      <ActionBar actions={actions}>
        <span className="muted attmonth__legend">A typed-in month is used for pay instead of the daily register.</span>
      </ActionBar>
      {note && <OkNote message={note} />}
      {problem && <ErrorNote message={problem} />}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted grid__empty">Nobody works here this month.</p>
      ) : (
        <div className="grid__wrap attmonth__wrap">
          <table className="table attmonth" aria-label={`Attendance for ${title}`}>
            <thead>
              <tr>
                <th className="attmonth__name">Person</th>
                {dates.map((d, i) => {
                  const holiday = holidays[d];
                  const wd = new Date(`${d}T00:00:00Z`).getUTCDay();
                  return (
                    <th
                      key={d}
                      className={`attmonth__day${holiday ? ' attmonth__day--holiday' : ''}`}
                      title={holiday ? `${shortDate(d)} · Holiday: ${holiday}` : shortDate(d)}
                    >
                      <button
                        type="button"
                        className="attmonth__daybtn"
                        disabled={d > today}
                        aria-label={`Open the register for ${shortDate(d)}`}
                        onClick={() => onOpenDate(d)}
                      >
                        {i + 1}
                        <small>{WEEKDAY_LETTER[wd]}</small>
                      </button>
                    </th>
                  );
                })}
                <th className="num" title="Days present">Present</th>
                <th className="num" title="Days absent">Absent</th>
                <th className="num" title="Half days">Half</th>
                <th className="num" title="Days on leave">Leave</th>
                <th className="num" title="Weekly offs and holidays">Off</th>
                <th className="num" title="Days not marked yet">Not marked</th>
                <th className="num" title="Overtime hours">OT hours</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const on = r.employeeId === chosenId;
                return (
                  <tr key={r.employeeId} className={on ? 'attmonth__row--chosen' : undefined}>
                    <th scope="row" className="attmonth__name">
                      <button
                        type="button"
                        className="attmonth__person"
                        aria-pressed={on}
                        onClick={() => {
                          setChosenId(on ? null : r.employeeId);
                          setNote(null);
                        }}
                      >
                        <span className="mono">{r.code}</span> {r.nameEn}
                      </button>
                      {r.override && (
                        <span className="pill pill--warn attmonth__badge" title={`Typed in: ${overrideText(r.override)}. Why: ${r.override.reason}`}>
                          Typed in
                        </span>
                      )}
                      {r.lockedBy && (
                        <span className="pill attmonth__badge" title={`Pay run ${r.lockedBy} covers this month`}>
                          <AppIcon kind="locked" size={12} />
                          Locked
                        </span>
                      )}
                    </th>
                    {r.cells.map((c, i) => {
                      const d = dates[i]!;
                      const holiday = holidays[d];
                      return (
                        <td
                          key={d}
                          className={`attmonth__cell attmonth__cell--${c ? cellKind(c.mark) : 'blank'}${holiday ? ' attmonth__cell--holiday' : ''}`}
                          title={cellTitle(d, c, holiday)}
                          onClick={() => {
                            if (d <= today) onOpenDate(d);
                          }}
                        >
                          {c ? MARK_CODE[c.mark] : ''}
                          {c?.overtime && (
                            <span className="attmonth__ot" aria-label="overtime">
                              •
                            </span>
                          )}
                          {c?.elsewhere && (
                            <span className="attmonth__elsewhere" aria-label="on another farm">
                              *
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="num">{r.totals.present}</td>
                    <td className="num">{r.totals.absent}</td>
                    <td className="num">{r.totals.halfDay}</td>
                    <td className="num">{r.totals.leave}</td>
                    <td className="num">{r.totals.off}</td>
                    <td className={`num${r.totals.unmarked > 0 ? ' attmonth__unmarked' : ''}`}>{r.totals.unmarked}</td>
                    <td className="num">{daysText(r.totals.overtimeHours)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {chosen && (
        <div className="attmonth__chosen">
          <strong>
            {chosen.code} — {chosen.nameEn}
          </strong>
          {chosen.override ? (
            <span>
              Typed-in month: {overrideText(chosen.override)}. Why: {chosen.override.reason}. Pay uses this, not the daily register.
            </span>
          ) : (
            <span className="muted">Pay uses the daily register for this month.</span>
          )}
        </div>
      )}
      <p className="muted attmonth__key">
        P present · A absent · H half day · L leave · O weekly off · HD holiday · • overtime · * worked on another farm. Click a day to open that day's register.
      </p>

      {typing && chosen && data && (
        <TypeInDialog
          row={chosen}
          year={data.year}
          month={data.month}
          hasDailyMarks={chosen.cells.some((c) => c !== null)}
          onClose={() => setTyping(false)}
          onDone={(message) => {
            invalidate();
            setProblem(null);
            setNote(message);
          }}
        />
      )}
    </>
  );
}

function TypeInDialog({
  row,
  year,
  month,
  hasDailyMarks,
  onClose,
  onDone,
}: {
  row: MonthRow;
  year: number;
  month: number;
  hasDailyMarks: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const o = row.override;
  const t = row.totals;
  const initial = o
    ? {
        presentDays: daysText(o.presentDays),
        absentDays: daysText(o.absentDays),
        paidLeaveDays: daysText(o.paidLeaveDays),
        unpaidLeaveDays: daysText(o.unpaidLeaveDays),
        overtimeHours: daysText(o.overtimeHours),
        reason: o.reason,
      }
    : {
        presentDays: daysText(t.present + t.halfDay / 2),
        absentDays: daysText(t.absent + t.halfDay / 2),
        paidLeaveDays: daysText(t.leave),
        unpaidLeaveDays: '0',
        overtimeHours: daysText(t.overtimeHours),
        reason: '',
      };
  const when = `${monthName(month)} ${year}`;
  return (
    <ActionDialog
      title={`Type in ${when}`}
      icon="month"
      subject={
        <DialogSubject
          icon="employee"
          code={row.code}
          name={row.nameEn}
          tags={[
            row.designation ? { icon: 'designation', label: row.designation } : null,
            { icon: 'people', label: WORKER_TYPE_TEXT[row.workerType] },
          ]}
          figure={{ label: 'Present on the register', value: daysText(row.totals.present + row.totals.halfDay / 2), unit: 'days' }}
        />
      }
      intro={
        <>
          A typed-in month is used for pay instead of the daily register. Days can be whole or half.
          {hasDailyMarks && ' This month already has daily marks; pay will ignore them while the typed-in month stands.'}
        </>
      }
      fields={[
        { key: 'presentDays', label: 'Days present', type: 'number', required: true },
        { key: 'absentDays', label: 'Days absent', type: 'number', required: true },
        { key: 'paidLeaveDays', label: 'Paid leave days', type: 'number', required: true },
        { key: 'unpaidLeaveDays', label: 'Unpaid leave days', type: 'number', required: true },
        { key: 'overtimeHours', label: 'Overtime hours', type: 'number', required: true },
        { key: 'reason', label: 'Why', type: 'textarea', required: true, placeholder: 'e.g. Farm keeps a paper register' },
      ]}
      initial={initial}
      submitLabel="Save typed-in month"
      onClose={onClose}
      onSubmit={async (v) => {
        await hrPut('/api/v1/hr/attendance/month-override', {
          employeeId: row.employeeId,
          year,
          month,
          presentDays: v.presentDays,
          absentDays: v.absentDays,
          paidLeaveDays: v.paidLeaveDays,
          unpaidLeaveDays: v.unpaidLeaveDays,
          overtimeHours: v.overtimeHours,
          reason: v.reason?.trim(),
        });
        onDone(`${when} typed in for ${row.nameEn}. Pay will use it instead of the daily register.`);
      }}
    />
  );
}
